var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { VirtualTable } from '@devexpress/dx-react-grid-material-ui';
import classNames from 'classnames';
import produce from 'immer';
import moment from 'moment-timezone';
import React, { useEffect } from 'react';
import { ImprovedDataTable } from '../Components/DataTable/ImprovedDataTable';
import { FieldIconMode } from '../Components/DatePicker';
import { FormikDatePicker } from '../Components/FormikFields/FormikDatePicker';
import { FormikTextField } from '../Components/FormikFields/FormikTextField';
import { LayoutCentral } from '../Components/LayoutCentral';
import { MedicationCard, MedicationCardMode } from '../Components/MedicationCard';
import { LoadingPlaceholder } from '../Components/Placeholder';
import { MedicationExpiry } from '../Contracts/MedicationModels';
import { ValidDateFormats } from '../Shared/DateUtilities';
import { expiryStatusFromDate, isInactiveMedicationStatus } from '../Shared/MedicationsHelpers';
import { backgroundAlt } from '../Styles/Colours';
import { getExpiresOn, getPerformedOn } from './StopMedication';
import styles from './StopMedicationsList.module.css';
var getColumns = function (allMedications) {
    return [
        {
            name: 'product',
            title: 'Product',
            getCellValue: function (row) {
                return (React.createElement(MedicationCard, { name: row.medication.name, strength: Number(row.medication.strength || '0'), strengthUnit: row.medication.strengthUnits, type: row.medication.type, selected: row.selected, size: row.medication.packAmount, productPackAmount: row.medication.productPackAmount, repeatsAmount: row.medication.repeatsAmount, mode: MedicationCardMode.Long, isNonStandardPackSize: row.medication.isNonStandardPackSize, nonStandardPackSize: row.medication.nonStandardPackSize }));
            }
        },
        {
            name: 'start',
            title: 'Start date',
            getCellValue: function (row) {
                if (row.selected)
                    return moment(getPerformedOn(moment(), row.medication.performedOn)).format(ValidDateFormats.DisplaySlashSpace);
                else
                    return moment(row.medication.performedOn).format(ValidDateFormats.DisplaySlashSpace);
            }
        },
        {
            name: 'until',
            title: 'End date',
            getCellValue: function (row) {
                var index = allMedications.findIndex(function (it) { return it.medication.id === row.medication.id; });
                return (React.createElement("span", { onClick: function (e) {
                        e.stopPropagation();
                        e.preventDefault();
                    } },
                    React.createElement(FormikDatePicker, { label: null, name: "medications[" + index + "].expiresOn", placeholder: ValidDateFormats.Edit, readOnly: !allMedications[index].selected, calendarIconMode: FieldIconMode.Embed, fillContainer: true, isClearable: false, format: ValidDateFormats.IsoFormat, hideWarningEmptyHelperText: true, testId: "date-picker-until-" + index })));
            }
        },
        {
            name: 'reason',
            title: 'Reason',
            getCellValue: function (row) {
                var index = allMedications.findIndex(function (it) { return it.medication.id === row.medication.id; });
                return (React.createElement("span", { onClick: function (e) {
                        e.stopPropagation();
                        e.preventDefault();
                    } },
                    React.createElement(FormikTextField, { label: null, name: "medications[" + index + "].reason", hideWarningEmptyHelperText: true, readOnly: !allMedications[index].selected, placeholder: "Type a reason...", testId: "text-input-reason-" + index })));
            }
        }
    ];
};
var RowComponent = function (props) {
    var _a;
    var _b, _c, _d, _e, _f, _g;
    var selectByRowClick = props.selectByRowClick, highlighted = props.highlighted, onToggle = props.onToggle, otherProps = __rest(props, ["selectByRowClick", "highlighted", "onToggle"]);
    var expiryStatus = expiryStatusFromDate(props.tableRow.row.medication.expiresOn);
    var isInactive = isInactiveMedicationStatus(props.tableRow.row.medication.status);
    var rowStyles = classNames(styles.row, (_a = {},
        _a[styles.selectedRow] = highlighted,
        _a[styles.rowExpired] = !isInactive && expiryStatus === MedicationExpiry.Expired,
        _a[styles.rowAboutToExpire] = !isInactive && expiryStatus === MedicationExpiry.AboutToExpire,
        _a));
    var medCode = (_d = (_c = (_b = props.tableRow.row) === null || _b === void 0 ? void 0 : _b.medication) === null || _c === void 0 ? void 0 : _c.id) !== null && _d !== void 0 ? _d : '';
    var medName = ((_g = (_f = (_e = props.tableRow.row) === null || _e === void 0 ? void 0 : _e.medication) === null || _f === void 0 ? void 0 : _f.name) !== null && _g !== void 0 ? _g : '').substr(0, 20);
    return (React.createElement(VirtualTable.Row, __assign({}, otherProps, { "data-test-med-code": medCode, "data-test-med-name": medName, row: props.tableRow.row, className: rowStyles })));
};
var CustomCellMemo = function (props) {
    var cellStyles = classNames(styles.cell, styles.interactive);
    return React.createElement(VirtualTable.Cell, __assign({}, props, { className: cellStyles, "data-test-id": props.column.name }));
};
var columnExtensions = [
    {
        columnName: 'product'
    },
    {
        columnName: 'start',
        width: 140
    },
    {
        columnName: 'until',
        width: 150
    },
    {
        columnName: 'reason',
        width: 200
    }
];
export var StopMedicationsList = function (props) {
    var selectedMedications = props.values.medications.filter(function (x) { return x.selected; });
    var selection = selectedMedications.map(function (x) { return x.medication.id; });
    useEffect(function () {
        props.values.medications.forEach(function (x, index) {
            // It is a selected item in the medication selection list.
            if (x.selected) {
                props.setFieldTouched("medications[" + index + "].expiresOn");
            }
        });
    }, []);
    var onTableDataSelectionChanged = function (selectedIds) {
        // Get all the difference (selected and unselected); between the original and new medication selection list.
        var difference = selection
            .filter(function (x) { return !selectedIds.includes(x); })
            .concat(selectedIds.filter(function (x) { return !selection.includes(x); }));
        var newState = produce(props.values.medications, function (draft) {
            draft.forEach(function (x) {
                var foundInList = difference.findIndex(function (it) { return it === x.medication.id; });
                if (foundInList !== -1) {
                    // It is a selected item in the medication selection list.
                    var selected = selectedIds.findIndex(function (it) { return it === x.medication.id; }) !== -1 ? true : false;
                    x.expiresOn = getExpiresOn(selected, moment(), x.medication.expiresOn);
                    x.selected = selected;
                }
            });
        });
        var newTouchedState = newState.map(function (x) {
            if (x.selected) {
                return { expiresOn: true };
            }
            else {
                return { expiresOn: false };
            }
        });
        props.setValues({ medications: newState });
        props.setTouched({ medications: newTouchedState });
    };
    if (props.values.medications != null && props.values.medications.length > 0) {
        return (React.createElement("div", { className: styles.tableContainer },
            React.createElement(ImprovedDataTable, { headerColour: backgroundAlt, columns: getColumns(props.values.medications), data: props.bulkSelectionMode ? props.values.medications : selectedMedications, cellComponent: CustomCellMemo, columnExtensions: columnExtensions, getRowId: function (item) { return item.medication.id; }, rowChecking: {
                    selectedIds: selection,
                    onSelectedChanged: onTableDataSelectionChanged,
                    showSelectAll: props.bulkSelectionMode ? true : false,
                    showSelectionColumn: props.bulkSelectionMode ? true : false,
                    rowComponent: RowComponent
                } })));
    }
    else {
        return (React.createElement(LayoutCentral, null,
            React.createElement(LoadingPlaceholder, null)));
    }
};
